import { useState, useEffect } from 'react';
import * as fmt from '../../i18n/format';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, Inbox, Clock, CheckCircle2, AlertCircle, MessageCircle } from 'lucide-react';
import { supportService } from '../../services/supportService';
import { useCopy, fill } from '../../i18n';
import type { SupportTicket, SupportTicketStatus } from '../../types/database';
import { cn } from '@/lib/utils';

interface TicketListProps {
  onViewTicket: (ticket: SupportTicket) => void;
  onNewTicket: () => void;
}

/**
 * Cor de cada status. A chave é o valor da coluna `status`; o rótulo vem do
 * dicionário (`account.support.statuses`), indexado por ela.
 */
const STATUS_COLORS: Record<SupportTicketStatus, { color: string; bgColor: string }> = {
  open: { color: '#3b82f6', bgColor: 'rgba(59,130,246,0.1)' },
  in_progress: { color: '#f59e0b', bgColor: 'rgba(245,158,11,0.1)' },
  waiting_user: { color: '#8b5cf6', bgColor: 'rgba(139,92,246,0.1)' },
  resolved: { color: '#22c55e', bgColor: 'rgba(34,197,94,0.1)' },
  closed: { color: '#6b7280', bgColor: 'rgba(107,114,128,0.1)' },
};

const TicketList = ({ onViewTicket, onNewTicket }: TicketListProps) => {
  const copy = useCopy().account.support;
  const t = copy.list;
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');

  useEffect(() => {
    loadTickets();
  }, [filter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const data = await supportService.getMyTickets(filter);
      setTickets(data);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return t.justNow;
    if (diffMin < 60) return fill(t.minutesAgo, { count: diffMin });
    if (diffHours < 24) return fill(t.hoursAgo, { count: diffHours });
    if (diffDays < 7) return fill(t.daysAgo, { count: diffDays });
    return fmt.date(date, { day: '2-digit', month: 'short' });
  };

  const getStatusIcon = (status: SupportTicketStatus) => {
    switch (status) {
      case 'open': return <AlertCircle size={14} />;
      case 'in_progress': return <Clock size={14} />;
      case 'waiting_user': return <MessageCircle size={14} />;
      case 'resolved': return <CheckCircle2 size={14} />;
      case 'closed': return <CheckCircle2 size={14} />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter tabs */}
      <div className="flex items-center gap-1 px-4 py-2.5 border-b border-[var(--color-border)]">
        {(['all', 'open', 'resolved'] as const).map(f => (
          <button
            key={f}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
              filter === f
                ? "bg-[var(--color-primary)] text-white"
                : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
            )}
            onClick={() => setFilter(f)}
          >
            {t.filters[f]}
          </button>
        ))}
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onNewTicket}>
          <Plus size={14} />
          {t.newButton}
        </Button>
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[var(--color-text-muted)]" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <Inbox size={40} className="text-[var(--color-text-muted)] mb-3 opacity-40" />
          <p className="text-sm font-medium text-[var(--color-text-secondary)]">
            {t.emptyTitle}
          </p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1 mb-4">
            {filter === 'all' ? t.emptyAll : filter === 'open' ? t.emptyOpen : t.emptyResolved}
          </p>
          <Button size="sm" onClick={onNewTicket}>
            <Plus size={14} />
            {t.createButton}
          </Button>
        </div>
      ) : (
        <div>
          {tickets.map(ticket => {
            const statusColor = STATUS_COLORS[ticket.status];
            return (
              <div
                key={ticket.id}
                className="support-ticket-item"
                onClick={() => onViewTicket(ticket)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] text-[var(--color-text-muted)] font-mono">
                      #{ticket.ticket_number}
                    </span>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {copy.categories[ticket.category as keyof typeof copy.categories] || ticket.category}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-[var(--color-text)] truncate">
                    {ticket.subject}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span
                      className="support-status-badge"
                      style={{ color: statusColor.color, background: statusColor.bgColor }}
                    >
                      {getStatusIcon(ticket.status)}
                      <span className="ml-1">{copy.statuses[ticket.status]}</span>
                    </span>
                  </div>
                </div>
                <span className="text-[11px] text-[var(--color-text-muted)] whitespace-nowrap mt-1">
                  {formatDate(ticket.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TicketList;
